/**
 * Las claves de caché, en un solo sitio.
 *
 * Están juntas porque la invalidación cruzada es la parte frágil: registrar
 * una respuesta cierra un requerimiento, cambia el estado del documento, el
 * del expediente, el del bloque y el del panel. Con las claves dispersas por
 * los componentes, tarde o temprano una de esas cinco se queda sin refrescar y
 * la pantalla miente.
 *
 * Convención: de lo general a lo particular, para que
 * `invalidateQueries({ queryKey: claves.expedientes.todos })` alcance a todos
 * los descendientes.
 */

export const claves = {
  sesion: ['sesion'] as const,
  catalogos: ['catalogos'] as const,

  panel: ['panel'] as const,

  bloques: {
    todos: ['bloques'] as const,
    uno: (id: string) => ['bloques', id] as const,
    expedientes: (id: string) => ['bloques', id, 'expedientes'] as const,
    politica: (id: string) => ['bloques', id, 'politica'] as const,
  },

  sorteos: {
    uno: (id: string) => ['sorteos', id] as const,
    semilla: ['sorteos', 'semilla'] as const,
  },

  expedientes: {
    todos: ['expedientes'] as const,
    uno: (id: string) => ['expedientes', id] as const,
    // Bajo `expedientes`: el motor los invalida con todo lo demás, porque una
    // respuesta tardía habilita el complementario.
    memorandos: (id: string) => ['expedientes', id, 'memorandos'] as const,
  },

  documentos: {
    todos: ['documentos'] as const,
    uno: (id: string) => ['documentos', id] as const,
    requerimientos: (id: string) => ['documentos', id, 'requerimientos'] as const,
  },

  requerimientos: {
    uno: (id: string) => ['requerimientos', id] as const,
  },

  entidades: {
    todas: ['entidades'] as const,
    lista: (incluirDesactivadas: boolean) => ['entidades', { incluirDesactivadas }] as const,
    una: (id: string) => ['entidades', id] as const,
  },

  circuito: {
    todo: ['circuito'] as const,
    colaMesaPartes: ['circuito', 'cola-mesa-partes'] as const,
    historialMesaPartes: ['circuito', 'historial-mesa-partes'] as const,
    asignadas: ['circuito', 'asignadas'] as const,
    notificadores: ['circuito', 'notificadores'] as const,
  },

  oci: {
    todo: ['derivaciones-oci'] as const,
    lista: (causa?: string) => ['derivaciones-oci', { causa: causa ?? 'todas' }] as const,
    pendientes: ['derivaciones-oci', 'pendientes'] as const,
  },

  configuracion: {
    politica: ['configuracion', 'politica'] as const,
    acta: ['configuracion', 'acta'] as const,
    documentos: ['configuracion', 'documentos'] as const,
    itemsSeleccion: ['configuracion', 'items-seleccion'] as const,
    calendario: ['calendario'] as const,
  },

  administracion: {
    roles: ['roles'] as const,
    permisos: ['permisos'] as const,
    usuarios: ['usuarios'] as const,
    excepciones: (usuarioId: string) => ['usuarios', usuarioId, 'permisos'] as const,
  },

  // El objeto de filtros entra tal cual en la clave: TanStack Query lo
  // serializa de forma estable, así que dos filtros iguales comparten caché.
  auditoria: (filtros: object) => ['auditoria', filtros] as const,
} as const

/**
 * Lo que hay que invalidar cuando el motor se mueve.
 *
 * Un paso, una respuesta o una anulación pueden cambiar el estado de cinco
 * agregados a la vez. Enumerarlos aquí, una vez, evita que cada mutación
 * decida por su cuenta —y se olvide de una.
 */
export const RAICES_DEL_MOTOR = [
  claves.panel,
  claves.bloques.todos,
  claves.expedientes.todos,
  claves.documentos.todos,
  claves.circuito.todo,
  claves.oci.todo,
] as const
